import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';

const BOOKING_URL =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=35c76793-be5c-432b-a8f4-ce81eb0bf158';

const heroImage =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/Castle-by-Candlelight.webp';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.4rem, 6vw, 4.6rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const HeroBookBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2.25rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 3px;
  background: ${C.gold};
  border-radius: 2px;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

const CastleByCandlelight = () => {
  return (
    <Page>
      <Helmet>
        <title>Deck The Halls — Belvoir Castle by Candlelight | Christmas 2025</title>
        <meta
          name="description"
          content="Experience Belvoir Castle by Candlelight every Thursday in December. The Christmas Experience with live musicians, bubbles and a mince pie on arrival in the Guard Room. 6pm – 8.30pm."
        />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/castle-by-candlelight" />
        <meta property="og:title" content="Deck The Halls — Belvoir Castle by Candlelight | Christmas 2025" />
        <meta
          property="og:description"
          content="A magical evening experience — Belvoir Castle by candlelight every Thursday in December. Live musicians, bubbles and a mince pie on arrival."
        />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/castle-by-candlelight" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Deck The Halls — Belvoir Castle by Candlelight" />
        <meta name="twitter:description" content="Every Thursday in December, 6pm – 8.30pm. Live musicians, bubbles and a mince pie on arrival in the Guard Room." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      {/* ── HERO ── */}
      <HeroWrap>
        <HeroImg src={heroImage} alt="Belvoir Castle by Candlelight" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Belvoir Castle · Every Thursday in December</HeroKicker>
          <HeroTitle>
            Deck The Halls<br /><em>Castle by Candlelight</em>
          </HeroTitle>
          <HeroStrap>
            The Christmas Experience by candlelight — live musicians, bubbles and a mince pie on arrival
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Every Thursday in December</Pill>
            <Pill>6pm – 8.30pm</Pill>
            <Pill>Live Musicians</Pill>
            <Pill>Free Flow</Pill>
          </BadgeRow>
          <HeroBookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            Book Now
          </HeroBookBtn>
        </HeroContent>
      </HeroWrap>

      {/* ── THE EXPERIENCE ── */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Experience</Kicker>
              <Divider />
              <SectionTitle>
                Belvoir Castle<br /><em>by Candlelight</em>
              </SectionTitle>
              <SectionLead>
                Step inside Belvoir Castle on a winter's evening and discover the magic of Deck the Halls by candlelight. Every Thursday throughout December, the Castle opens its doors for a special evening experience — the grand rooms glowing with candlelight, adorned in their full Christmas splendour.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Begin your evening with a warm welcome in the Guard Room — a glass of bubbles and a mince pie on arrival — before being led through grand rooms, each dressed as a different Christmas carol, accompanied by the beautiful sound of live musicians.
              </SectionLead>
            </div>
            <div style={{
              borderRadius: '14px',
              overflow: 'hidden',
              height: 'clamp(280px, 40vw, 420px)',
              background: C.navy
            }}>
              <img
                src={heroImage}
                alt="Belvoir Castle by Candlelight interior"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </TwoCols>
        </Inner>
      </Section>

      {/* ── WHAT'S INCLUDED ── */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What's Included</Kicker>
          <Divider />
          <SectionTitle>
            A Magical <em>Evening Awaits</em>
          </SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon />
              <FeatureTitle>Bubbles on Arrival</FeatureTitle>
              <FeatureBody>
                Begin your evening with a glass of bubbles in the historic Guard Room — the perfect festive welcome to Belvoir Castle.
              </FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon />
              <FeatureTitle>Live Musicians</FeatureTitle>
              <FeatureBody>
                Wander through the Castle's grand rooms accompanied by the enchanting sound of live musicians, setting the perfect Christmas atmosphere.
              </FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon />
              <FeatureTitle>Mince Pie on Arrival</FeatureTitle>
              <FeatureBody>
                A traditional mince pie greets you as you arrive — a warm and festive treat to start your candlelit evening at the Castle.
              </FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon />
              <FeatureTitle>Deck the Halls by Candlelight</FeatureTitle>
              <FeatureBody>
                Explore grand rooms each themed as a different Christmas carol, all bathed in the warm glow of candlelight for an unforgettable festive atmosphere.
              </FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon />
              <FeatureTitle>Free Flow</FeatureTitle>
              <FeatureBody>
                Enjoy free-flow drinks throughout the evening as you explore the Castle at your own pace — a truly indulgent Christmas night out.
              </FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon />
              <FeatureTitle $light>Belvoir Castle in all its Christmas Glory</FeatureTitle>
              <FeatureBody $light>
                Exclusive evening access to one of England's finest historic castles, dressed in its most spectacular Christmas finery. A night to remember.
              </FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      {/* ── ESSENTIAL INFORMATION ── */}
      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <InfoValue>Every Thursday<br />in December</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Times</InfoLabel>
              <InfoValue>6pm – 8.30pm</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Includes</InfoLabel>
              <InfoValue>Bubbles, Mince Pie<br />&amp; Live Music</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Drinks</InfoLabel>
              <InfoValue>Free Flow<br />All Evening</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <InfoValue>Essential —<br />Limited Spaces</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      {/* ── NEWSLETTER ── */}
      <div id="newsletter" style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Castle by Candlelight" />
      </div>

      {/* ── BOTTOM CTA ── */}
      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Book Your <em>Candlelit Evening</em>
          </CtaTitle>
          <CtaBody>
            Spaces are limited for this magical evening experience. Secure your place for Castle by Candlelight — every Thursday in December, 6pm to 8.30pm. Bubbles, live music and the full magic of Deck the Halls await.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Book Now
            </CtaBtn>
            <CtaSecondary to="/christmas/your-festive-visit">
              Plan Your Visit
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Every Thursday in December · 6pm – 8.30pm · Belvoir Castle, Grantham NG32 1PE
          </CtaMeta>
        </CtaInner>
      </CtaSection>

      <Footer />
    </Page>
  );
};

export default CastleByCandlelight;
